import { Box, Button, Stack } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';

import { OrderDirection } from '#features/api/types';
import { blogPostsQueryOptions } from '#features/blog/model/blog';
import {
  getGameSettingsQueryOptions,
  getGamesQueryOptions,
  type Game,
} from '#features/game/model/services';
import { landingPageQueryOptions } from '#features/landing/model/landing';
import { parseGameFacets } from '#features/landing/utils/gameFacets';
import { AppFooter } from '#features/layout/components/AppFooter';
import { getResizedImageUrl } from '#features/ui/components/Image/loader';
import { AllGames } from './AllGames';
import { BlogPosts } from './BlogPosts';
import { Cta } from './Cta';
import { DiscordCommunity } from './DiscordCommunity';
import { FAQ } from './FAQ';
import { GameConnect } from './GameConnect';
import { GameFacets } from './GameFacets';
import { GameStats, hasGameStats } from './GameStats';
import { GridGlowBackground } from './GridGlowBackground';
import { Hero } from './Hero';
import { HowItWorks } from './HowItWorks';
import { InlineCta } from './InlineCta';
import { LfgBanners } from './LfgBanners';
import { MobileApp } from './MobileApp';
import { PlayYourWay } from './PlayYourWay';
import { PopularGames } from './PopularGames';
import { StickyMobileCta } from './StickyMobileCta';
import { TrustStrip } from './TrustStrip';
import { ValueProp } from './ValueProp';

type LandingProps = {
  gameSlug: string;
  heroImage?: string;
};

const ACCENT_FALLBACK = '#4B50E6';

/** Strip a leading emoji/symbol prefix ("🌍 Europe" → "Europe", "☠️ Action seeker" → "Action seeker"). */
const stripLead = (value: string | null | undefined): string | undefined =>
  value?.replace(/^[^\p{L}\p{N}]+/u, '').trim() || undefined;

const SectionDivider = ({ accent }: { accent?: string }) => {
  const c = accent ?? ACCENT_FALLBACK;
  return (
    <Box
      w="100%"
      h="1px"
      background={`linear-gradient(90deg, transparent 0%, ${c}4D 30%, ${c}4D 70%, transparent 100%)`}
    />
  );
};

/**
 * Full-bleed background band for a section: the background stretches edge-to-edge
 * (100vw breakout, same trick as the hero) while the content stays in the grid.
 * Used to separate blocks by colour so the page isn't one flat dark sheet.
 * When `accent` is set, a faint game-coloured wash is layered on top so each
 * game's page carries its own atmosphere instead of one flat dark sheet.
 */
const SectionBand = ({
  background,
  accent,
  children,
}: {
  background: string;
  accent?: string;
  children: React.ReactNode;
}) => (
  <Box
    position="relative"
    w="100vw"
    ml="calc(50% - 50vw)"
    mr="calc(50% - 50vw)"
    background={background}
  >
    {accent && (
      <Box
        position="absolute"
        inset={0}
        pointerEvents="none"
        background={`radial-gradient(ellipse 80% 60% at 50% 0%, ${accent}14 0%, transparent 70%)`}
      />
    )}
    <Box
      position="relative"
      maxW={{ base: '640px', lg: '1200px' }}
      mx="auto"
      px={{ base: 4, md: 6, lg: 8 }}
    >
      {children}
    </Box>
  </Box>
);

// Subtly elevated dark band - a few % lighter than the base page, no hard edges.
const BAND_ELEVATED =
  'linear-gradient(180deg, hsla(228, 11.6%, 11.5%, 1) 0%, hsla(228, 11.6%, 9.5%, 1) 100%)';

/** Fallback for admin colors that are #000000 / empty (invisible on dark bg) */
const safeColor = (color: string | null | undefined, fallback: string): string => {
  if (!color) return fallback;
  const c = color.toLowerCase();
  if (c === '#000000' || c === '#000' || c === 'black' || c === 'transparent' || c === '') {
    return fallback;
  }
  return color;
};

export const Landing = ({ gameSlug, heroImage }: LandingProps) => {
  const { data } = useSuspenseQuery({
    ...landingPageQueryOptions(gameSlug),
    refetchOnMount: false,
  });
  const { data: blogPosts } = useSuspenseQuery({
    ...blogPostsQueryOptions({
      limit: 3,
      offset: 0,
      orders: [{ direction: OrderDirection.DESC, field: 'date' }],
    }),
    refetchOnMount: false,
  });
  const { data: allGames } = useSuspenseQuery({
    ...getGamesQueryOptions(),
    refetchOnMount: false,
  });

  const { t } = useLingui();
  const isIndex = gameSlug === 'index';

  // Current game record (for logo, platforms, id → settings). Undefined on the index page.
  const currentGame = isIndex
    ? undefined
    : (allGames ?? []).find(
        (game): game is Extract<Game, { __typename: 'Game' }> =>
          game?.__typename === 'Game' && game.slug === gameSlug
      );

  // Per-game admin facets (ranks, legends/agents, roles, regions, modes).
  const { data: gameSettings } = useQuery({
    ...getGameSettingsQueryOptions({ id: currentGame?.id ?? '' }),
    enabled: Boolean(currentGame?.id),
  });

  if (!data) {
    return null;
  }

  const accentColor = safeColor(data.mainColor, ACCENT_FALLBACK);
  const realGames = allGames?.filter((game) => game?.__typename === 'Game') ?? [];
  const gamesCount = realGames.length;

  // Normalized game facets + hero chip values.
  const facets = parseGameFacets(gameSettings);
  const heroCharacter = facets.characters.find((c) => c.image) ?? facets.characters[0];
  const heroRank = facets.ranks[facets.ranks.length - 1]?.name;
  const heroRole = stripLead(facets.roles[0]?.name) ?? facets.characters[0]?.name;
  const heroRegion = stripLead(facets.regions[0]?.name);
  const gameName = data.name ?? '';
  // Real icons for the games shown in the Discover mock (step 2 of How It Works).
  const mockGameSlugs = new Set(['call-of-duty-warzone-2', 'fortnite', 'apex-legends']);
  const gameIcons = Object.fromEntries(
    realGames
      .filter((game) => game.slug && mockGameSlugs.has(game.slug))
      .map((game) => [
        game.slug,
        { icon: game.icon, iconColor: game.iconColor, image: game.mobileImage, name: game.name },
      ])
  );
  // Games shown in the create-post game picker (step 1) - real games with icons, popular ones first.
  const gamesWithIcon = realGames.filter((game) => game.icon && game.slug);
  const pickerOrder = [
    'apex-legends',
    'fortnite',
    'arc-raiders',
    'call-of-duty-warzone-2',
    'valorant',
    'cs2',
    'the-finals',
    'overwatch',
  ];
  const pickerRank = (slug: string | null | undefined) => {
    // On a game page put the current game first so the step-1 mock highlights it.
    if (!isIndex && slug === gameSlug) return -1;
    const i = pickerOrder.indexOf(slug ?? '');
    return i === -1 ? 999 : i;
  };
  const pickerGames = gamesWithIcon
    .toSorted((a, b) => pickerRank(a.slug) - pickerRank(b.slug))
    .slice(0, 10)
    .map((game) => ({
      icon: game.icon,
      iconColor: game.iconColor,
      name: game.name,
      slug: game.slug,
    }));

  // Step-1 "create post" mock: feature the current game (name/icon/accent + which picker
  // tile is selected) instead of the hardcoded Apex default, and seed rank/mode/region from
  // the game's own facets. Falls back to Apex on the index page (no current game).
  const mockFeaturedGame = currentGame
    ? {
        icon: currentGame.icon,
        iconColor: currentGame.iconColor,
        image: currentGame.mobileImage,
        name: currentGame.name,
        slug: currentGame.slug,
      }
    : gameIcons['apex-legends'];
  const mockFeaturedSlug = currentGame?.slug ?? 'apex-legends';

  return (
    <Stack
      position="relative"
      m="0 auto"
      gap={0}
      w="100%"
      maxW={{ base: '640px', lg: '1200px' }}
      px={{ base: 4, md: 6, lg: 8 }}
      mb={4}
      // Landing-scoped contrast bump: the default muted/subtle foreground tokens
      // (0.36 / 0.16 alpha) fail WCAG AA on the dark background. Overriding the
      // CSS vars here brightens body/label text across the whole landing (incl.
      // its footer) without touching the app-wide theme tokens.
      // The purple overrides do the same for the brand accent: a brighter purple
      // for text-on-dark (labels, accents) and a slightly darker solid so white
      // button labels clear AA - all scoped to the landing, brand palette untouched.
      css={{
        '--chakra-colors-fg-muted': 'rgba(255, 255, 255, 0.55)',
        '--chakra-colors-fg-subtle': 'rgba(255, 255, 255, 0.5)',
        '--chakra-colors-purple-300': '#8288f5',
        '--chakra-colors-purple-200': '#8288f5',
        '--chakra-colors-purple-fg': '#8288f5',
        '--chakra-colors-purple-solid': '#5158e2',
      }}
    >
      {/* 1. Hero - full-bleed animated background, content stays in the 1200px grid.
          `w=100vw` + negative `margin-inline` breaks out of the constrained Stack;
          the ancestor `overflowX="clip"` in MainLayout prevents any horizontal scroll.
          Negative `mt` pulls the background up under the header, removing the seam. */}
      <Box
        position="relative"
        w="100vw"
        ml="calc(50% - 50vw)"
        mr="calc(50% - 50vw)"
        mt={{ base: -5, lg: -5 }}
      >
        <GridGlowBackground glowColors={[accentColor, '#8E7BFF', accentColor]}>
          <Box
            maxW={{ base: '640px', lg: '1200px' }}
            mx="auto"
            px={{ base: 4, md: 6, lg: 8 }}
            py={{ base: 8, lg: 12 }}
          >
            <Hero
              name={isIndex ? '' : gameName}
              color={data.mainColor ?? ''}
              image={heroImage ?? data.mainImage ?? ''}
              rank={heroRank}
              role={heroRole}
              region={heroRegion}
              characterImage={heroCharacter?.image ?? undefined}
              characterName={heroCharacter?.name}
            />
          </Box>
        </GridGlowBackground>
      </Box>

      {/* 2. Trust strip - social-proof numbers as the first beat under the hero. */}
      <Box py={6}>
        <TrustStrip gamesCount={gamesCount} />
      </Box>

      {/* 2b. Popular games - "is my game here?" right after the trust numbers, with a jump
          to the full A-Z list lower down. Index only; game pages lead with their own game
          and keep the browse block near the footer. */}
      {isIndex && data.popularGames && data.popularGames.length > 0 && (
        <Box pt={{ base: 2, md: 4 }} pb={{ base: 6, md: 8 }}>
          <PopularGames games={data.popularGames} />
          {allGames && allGames.length > 5 && (
            <Box display="flex" justifyContent="center" mt={{ base: 5, md: 6 }}>
              <Button asChild variant="outline" size="sm">
                <a href="#all-games">{t`See all games`} ↓</a>
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* 3. Pain - elevated colour band */}
      <SectionBand background={BAND_ELEVATED} accent={isIndex ? undefined : accentColor}>
        <ValueProp
          image={data.teamworkImage}
          bgColor={data.bgColor ?? undefined}
          accentColor={data.mainColor ?? undefined}
          gameName={isIndex ? undefined : gameName}
        />
      </SectionBand>

      {/* 3b. Game-aware matching (game pages) - placed right after the pain: "here's how we
          solve it FOR YOUR GAME". Real per-game criteria from admin over the game's key art,
          so the game's relevance is proven before any generic product content. */}
      {!isIndex &&
        (facets.ranks.length > 0 ||
          facets.characters.length > 0 ||
          facets.roles.length > 0 ||
          facets.gameModes.length > 0 ||
          facets.regions.length > 0 ||
          facets.versions.length > 0) && (
          <Box
            position="relative"
            w="100vw"
            ml="calc(50% - 50vw)"
            mr="calc(50% - 50vw)"
            overflow="hidden"
            borderTop="1px solid"
            borderBottom="1px solid"
            borderColor="whiteAlpha.100"
          >
            {currentGame?.desktopImage && (
              <>
                {/* CSS backgrounds skip the <Image> pipeline, so resize by hand - the raw
                  asset is a full-size PNG (~700 KiB) for what renders as a dim backdrop. */}
                <Box
                  position="absolute"
                  inset={0}
                  backgroundImage={`url(${getResizedImageUrl(currentGame.desktopImage, 1600, {}, 'webp')})`}
                  backgroundSize="cover"
                  backgroundPosition="center"
                  opacity={0.55}
                  pointerEvents="none"
                />
                {/* Scrim shaped around the content instead of a flat veil: near-opaque behind
                  the centred copy (contrast), thin at the left/right bleed and top/bottom
                  edges so the game's key art is actually recognisable there. Mobile has no
                  bleed - the copy spans the full width - so it keeps a flat veil. */}
                {/* Two boxes rather than one responsive `background`: a multi-layer gradient
                  value in a breakpoint object gets dropped by Chakra's shorthand parsing. */}
                <Box
                  position="absolute"
                  inset={0}
                  pointerEvents="none"
                  display={{ base: 'block', md: 'none' }}
                  background="linear-gradient(180deg, hsla(228,11.6%,7.5%,0.9) 0%, hsla(228,11.6%,7.5%,0.8) 50%, hsla(228,11.6%,7.5%,0.9) 100%)"
                />
                <Box
                  position="absolute"
                  inset={0}
                  pointerEvents="none"
                  display={{ base: 'none', md: 'block' }}
                  background="radial-gradient(ellipse 62% 78% at 50% 50%, hsla(228,11.6%,7.5%,0.93) 0%, hsla(228,11.6%,7.5%,0.74) 58%, hsla(228,11.6%,7.5%,0.3) 100%), linear-gradient(180deg, hsla(228,11.6%,7.5%,0.85) 0%, hsla(228,11.6%,7.5%,0.25) 22%, hsla(228,11.6%,7.5%,0.25) 78%, hsla(228,11.6%,7.5%,0.85) 100%)"
                />
              </>
            )}
            <Box
              position="relative"
              maxW={{ base: '640px', lg: '1200px' }}
              mx="auto"
              px={{ base: 4, md: 6, lg: 8 }}
              py={{ base: 12, md: 16 }}
            >
              <GameFacets gameName={gameName} facets={facets} accentColor={accentColor} />
            </Box>
          </Box>
        )}

      {/* 3c. Game stats (only games where we actually collect stats) - right after "what we
          match you on": матчинг по параметрам заявки, а сразу под ним - чем эти параметры
          подтверждаются. Условие снаружи, а не только внутри компонента: иначе на играх без
          статистики от секции остались бы её вертикальные отступы - дыра в 100+ px. */}
      {!isIndex && hasGameStats(gameSlug) && (
        <Box py={{ base: 12, md: 16 }}>
          <GameStats gameName={gameName} gameSlug={gameSlug} accentColor={accentColor} />
        </Box>
      )}

      {/* 4. How It Works - base */}
      <Box py={{ base: 12, md: 16 }}>
        <HowItWorks
          accentColor={accentColor}
          gameIcons={gameIcons}
          pickerGames={pickerGames}
          featuredGame={mockFeaturedGame}
          featuredSlug={mockFeaturedSlug}
        />
      </Box>

      {/* 4b. Mid CTA - right after the visitor sees how simple it is */}
      <Box pb={{ base: 12, md: 16 }}>
        <InlineCta
          title={t`v3.cta.mid1.title`}
          buttonLabel={t`v3.cta.mid1.button`}
          accentColor={accentColor}
        />
      </Box>

      {/* 5. Play your way - merged "three ways to squad up" (modes) + the tools that power
          every post. Replaces the old Showcase + Features duplication. Elevated band. */}
      <SectionBand background={BAND_ELEVATED} accent={isIndex ? undefined : accentColor}>
        <Box py={{ base: 12, md: 16 }}>
          <PlayYourWay />
        </Box>
      </SectionBand>

      {/* 6b. LFG banners - base (kept off the elevated band so PlayYourWay's band above
          stays its own beat instead of merging into one long panel with this one). */}
      <Box py={{ base: 12, md: 16 }}>
        <LfgBanners />
      </Box>

      {/* 7. Full A-Z games list (index) - crawlable linked roster near the page end, and
          the anchor target for the hero "See all games" jump. Game pages get their own
          grouped browse block after the final CTA (below) instead of it sitting mid-page. */}
      {isIndex && allGames && allGames.length > 0 && (
        <Box id="all-games" py={{ base: 12, md: 16 }} css={{ scrollMarginTop: '80px' }}>
          <AllGames games={allGames} currentSlug={gameSlug} />
        </Box>
      )}
      {/* 7b. Mid CTA - high-intent moment: the visitor just found their game */}
      {isIndex && (
        <Box pt={{ base: 6, md: 8 }} pb={{ base: 12, md: 16 }}>
          <InlineCta
            title={t`v3.cta.mid2.title`}
            buttonLabel={t`v3.cta.mid2.button`}
            accentColor={accentColor}
          />
        </Box>
      )}

      {/* 7c. Game connect band - unique keyword-rich prose on every game page (SEO body copy
          for "team finder / group finder / duo"). Uses the admin connectImage when set. */}
      {!isIndex && (
        <Box py={{ base: 12, md: 16 }}>
          <GameConnect gameName={gameName} image={data.connectImage} accentColor={accentColor} />
        </Box>
      )}

      {/* 8. FAQ - elevated colour band (+ FAQPage JSON-LD) */}
      <SectionBand background={BAND_ELEVATED} accent={isIndex ? undefined : accentColor}>
        <Box py={{ base: 12, md: 16 }}>
          {/* On the homepage the API returns a generic name ("online gaming"),
              so pass an empty gameName to keep the generic FAQ (same guard as Hero). */}
          <FAQ gameName={isIndex ? '' : (data.name ?? '')} />
        </Box>
      </SectionBand>

      {/* 9. Final CTA - base */}
      <Box py={{ base: 12, md: 16 }}>
        <Cta accentColor={accentColor} />
      </Box>

      {/* Explore other games (game pages) - one grouped browse block after the final CTA:
          the popular carousel + the full A-Z linked roster (current game excluded). Kept
          together as secondary navigation instead of scattered mid-page. */}
      {!isIndex && (
        <>
          {data.popularGames && data.popularGames.length > 0 && (
            <Box py={{ base: 12, md: 16 }}>
              <PopularGames games={data.popularGames} />
            </Box>
          )}
          {allGames && allGames.length > 0 && (
            <Box id="all-games" py={{ base: 12, md: 16 }} css={{ scrollMarginTop: '80px' }}>
              <AllGames games={allGames} currentSlug={gameSlug} />
            </Box>
          )}
        </>
      )}

      {/* 11. Blog */}
      {blogPosts && blogPosts.records.length > 0 && (
        <>
          <SectionDivider accent={isIndex ? undefined : accentColor} />
          <Box py={{ base: 12, md: 16 }}>
            <BlogPosts posts={blogPosts.records} />
          </Box>
        </>
      )}

      {/* 11b. Community - Discord join + built-by-gamers */}
      <SectionDivider accent={isIndex ? undefined : accentColor} />
      <Box py={{ base: 12, md: 16 }}>
        <DiscordCommunity />
      </Box>

      {/* 11c. Mobile app - app download as the final content beat before the footer */}
      <SectionDivider accent={isIndex ? undefined : accentColor} />
      <Box py={{ base: 12, md: 16 }}>
        <MobileApp />
      </Box>

      {/* 12. Footer */}
      <AppFooter />

      {/* Persistent mobile CTA */}
      <StickyMobileCta label={t`v3.hero.ctaPrimary`} accentColor={accentColor} />
    </Stack>
  );
};
